import { type DefaultContext, mutationPromise } from '@egw/clients'
import {
  type EnrollmentHYCSWelcomeData,
  type EnrollmentHYCSWelcomeSubmitInput,
  type EnrollmentHYCSWelcomeSubmitOutput,
} from '../../../../models'
import { createMutationHook, createQueryHook } from '../../../../hooks'

/**
 * Get Enrollment HYCS Welcome data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentHYCSWelcomeData>
 * @example const response = await getEnrollmentHYCSWelcomeData()
 */
export const getEnrollmentHYCSWelcomeData = async (context?: DefaultContext): Promise<EnrollmentHYCSWelcomeData> => {
  const response = (await mutationPromise(
    {
      name: 'getEnrollmentHYCSWelcomeData',
      query: `mutation getEnrollmentHYCSWelcomeData($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            path
            enrollmentData {
              applicationId
              firstName
            }
          }
          ... on SubmitEnrollmentError {
            __typename
            path
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: {},
    },
    context,
  )) as { submitEnrollment: EnrollmentHYCSWelcomeData }
  return response.submitEnrollment
}

export const useGetEnrollmentHYCSWelcomeData = createQueryHook<undefined, EnrollmentHYCSWelcomeData>(
  getEnrollmentHYCSWelcomeData,
)

/**
 * Submit Enrollment HYCS Welcome data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentHYCSWelcomeSubmitOutput>
 * @example const response = await submitEnrollmentHYCSWelcomeData();
 */
export const submitEnrollmentHYCSWelcomeData = async (
  enrollmentWelcomeSubmitInput: EnrollmentHYCSWelcomeSubmitInput,
  context?: DefaultContext,
): Promise<EnrollmentHYCSWelcomeSubmitOutput> => {
  const response = (await mutationPromise(
    {
      name: 'submitEnrollmentHYCSWelcomeData',
      query: `mutation submitEnrollmentHYCSWelcomeData($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            path
          }
          ... on SubmitEnrollmentError {
            __typename
            path
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: enrollmentWelcomeSubmitInput,
    },
    context,
  )) as { submitEnrollment: EnrollmentHYCSWelcomeSubmitOutput }
  return response.submitEnrollment
}

export const useSubmitEnrollmentHYCSWelcomeData = createMutationHook<
  EnrollmentHYCSWelcomeSubmitInput,
  EnrollmentHYCSWelcomeSubmitOutput
>(submitEnrollmentHYCSWelcomeData)
